<template>
  <div class="ov-page">
    <!-- ===== EN-TÊTE (le titre est déjà affiché dans la barre d'application) ===== -->
    <div class="ov-header">
      <div v-if="typeStrategieBagage !== undefined" class="ov-note ov-note--warning ov-header__note">
        <v-icon small color="#b45309">mdi-alert-circle-outline</v-icon>
        <span><strong>{{ $t('luggageStrategy.note') }}</strong> {{ $t('luggageStrategy.revokeNote') }}</span>
      </div>
      <div v-else class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('luggageStrategy.subtitle') }} — {{ $t('luggageStrategy.description') }}</span>
      </div>
    </div>

    <!-- ===== STRATÉGIE EXISTANTE ===== -->
    <div v-if="typeStrategieBagage !== undefined" class="ov-grid">
      <div class="ov-stack">
        <!-- ── PAR POIDS ── -->
        <div
          v-if="typeStrategieBagage.typeStrategieBagageDesignation === 'typeStrategieBagagePoids'"
          class="ov-card"
        >
          <div class="ov-card__head">
            <div class="ov-card__title">
              <v-icon small color="#159e72">mdi-weight-kilogram</v-icon>
              {{ $t('luggageStrategy.currentStrategyByWeight') }}
            </div>
            <span class="ov-badge ov-badge--success">{{ $t('luggageStrategy.active') }}</span>
          </div>
          <div class="ov-card__body">
            <StrategieBagagePoids
              :type-strategie="typeStrategieBagage.typeStrategieBagageDesignation"
              :existing-strategy="typeStrategieBagage"
              @strategie-updated="handleStrategieCreated"
            />
          </div>
        </div>

        <!-- ── PAR TYPE ── -->
        <div
          v-else-if="typeStrategieBagage.typeStrategieBagageDesignation === 'typeStrategieBagageType'"
          class="ov-card"
        >
          <div class="ov-card__head">
            <div class="ov-card__title">
              <v-icon small color="#159e72">mdi-tag-multiple-outline</v-icon>
              {{ $t('luggageStrategy.currentStrategyByType') }}
            </div>
            <span class="ov-badge ov-badge--success">{{ $t('luggageStrategy.active') }}</span>
          </div>
          <div class="ov-card__body">
            <StrategieBagageType
              :typeStrategie="typeStrategieBagage.typeStrategieBagageDesignation"
              :existing-strategy="typeStrategieBagage"
              @strategie-updated="handleStrategieCreated"
            />
          </div>
        </div>
      </div>

      <!-- Résumé -->
      <div
        v-if="
          typeStrategieBagage.typeStrategieBagageDesignation === 'typeStrategieBagagePoids' ||
          typeStrategieBagage.typeStrategieBagageDesignation === 'typeStrategieBagageType'
        "
        class="ov-sticky"
      >
        <div class="ov-card">
          <div class="ov-card__head">
            <div class="ov-card__title">{{ $t('luggageStrategy.policy') }}</div>
          </div>
          <div class="ov-card__body">
            <div class="ov-stat">
              <span class="ov-stat__label">{{ $t('luggageStrategy.type') }}</span>
              <span class="ov-stat__value">
                {{ typeStrategieBagage.typeStrategieBagageDesignation === 'typeStrategieBagagePoids' ? 'Par poids' : 'Par type' }}
              </span>
            </div>
            <template v-if="typeStrategieBagage.typeStrategieBagageDesignation === 'typeStrategieBagagePoids'">
              <div class="ov-stat">
                <span class="ov-stat__label">{{ $t('luggageStrategy.pricePerKilo') }}</span>
                <span class="ov-stat__value ov-stat__value--accent">{{ currentPoidsDTO.prixKilo || 0 }} FCFA</span>
              </div>
              <div class="ov-stat">
                <span class="ov-stat__label">{{ $t('luggageStrategy.nonTaxableKilos') }}</span>
                <span class="ov-stat__value">{{ currentPoidsDTO.nombreKiloNonTaxable || 0 }} kg</span>
              </div>
            </template>
            <div v-else class="ov-stat">
              <span class="ov-stat__label">Types de bagages</span>
              <span class="ov-count">{{ currentTypeLines.length }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== CRÉATION D'UNE NOUVELLE STRATÉGIE ===== -->
    <div v-else class="ov-grid">
      <div class="ov-stack">
        <!-- ÉTAPE 1 : Sélection du type -->
        <div class="ov-card">
          <div class="ov-card__head">
            <div class="ov-card__title">{{ $t('luggageStrategy.selectStrategy') }}</div>
          </div>
          <div class="ov-card__body">
            <div class="csb-tiles">
              <button
                type="button"
                class="csb-tile"
                :class="{ 'csb-tile--selected': strategieSelected === 'typeStrategieBagagePoids' }"
                @click="selectStrategie('typeStrategieBagagePoids')"
              >
                <span class="csb-tile__check">
                  <v-icon v-if="strategieSelected === 'typeStrategieBagagePoids'" x-small color="white">mdi-check</v-icon>
                </span>
                <span class="csb-tile__body">
                  <span class="csb-tile__title">
                    <v-icon small color="#159e72">mdi-weight-kilogram</v-icon>
                    Stratégie par Poids
                  </span>
                  <span class="csb-tile__desc">
                    Tarification au kilogramme. Définissez un prix par kg et un quota de kilos gratuits par voyageur.
                  </span>
                  <span class="csb-tile__tags">
                    <span class="ov-badge ov-badge--navy ov-badge--plain">Prix/kg</span>
                    <span class="ov-badge ov-badge--navy ov-badge--plain">Quota gratuit</span>
                  </span>
                </span>
              </button>

              <button
                type="button"
                class="csb-tile"
                :class="{ 'csb-tile--selected': strategieSelected === 'typeStrategieBagageType' }"
                @click="selectStrategie('typeStrategieBagageType')"
              >
                <span class="csb-tile__check">
                  <v-icon v-if="strategieSelected === 'typeStrategieBagageType'" x-small color="white">mdi-check</v-icon>
                </span>
                <span class="csb-tile__body">
                  <span class="csb-tile__title">
                    <v-icon small color="#159e72">mdi-tag-multiple-outline</v-icon>
                    Stratégie par Type
                  </span>
                  <span class="csb-tile__desc">
                    Tarification selon la catégorie de bagage (cabine, soute, etc.). Un prix fixe par type de bagage.
                  </span>
                  <span class="csb-tile__tags">
                    <span class="ov-badge ov-badge--navy ov-badge--plain">Cabine</span>
                    <span class="ov-badge ov-badge--navy ov-badge--plain">Soute</span>
                    <span class="ov-badge ov-badge--navy ov-badge--plain">Spécial</span>
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>

        <!-- ÉTAPE 2 : Configuration par poids -->
        <div v-if="strategieSelected === 'typeStrategieBagagePoids'" class="ov-card">
          <div class="ov-card__head">
            <div>
              <div class="ov-card__kicker">2 · {{ $t('luggageStrategy.configuration') }}</div>
              <div class="ov-card__title">{{ $t('luggageStrategy.configurationByWeight') }}</div>
            </div>
          </div>
          <div class="ov-card__body">
            <StrategieBagagePoids :type-strategie="strategieSelected" @strategie-created="handleStrategieCreated" />
          </div>
        </div>

        <!-- ÉTAPE 2 : Configuration par type -->
        <div v-if="strategieSelected === 'typeStrategieBagageType'" class="ov-card">
          <div class="ov-card__head">
            <div>
              <div class="ov-card__kicker">2 · {{ $t('luggageStrategy.configuration') }}</div>
              <div class="ov-card__title">{{ $t('luggageStrategy.configurationByType') }}</div>
            </div>
          </div>
          <div class="ov-card__body">
            <StrategieBagageType :typeStrategie="strategieSelected" @strategie-created="handleStrategieCreated" />
          </div>
        </div>
      </div>

      <!-- COLONNE GUIDE -->
      <div class="ov-sticky">
        <div class="ov-card">
          <div class="ov-card__head">
            <div class="ov-card__title">
              <v-icon small color="#159e72">mdi-lightbulb-on-outline</v-icon>
              {{ $t('luggageStrategy.tips') }}
            </div>
          </div>
          <div class="ov-card__body">
            <div class="ov-stat">
              <span class="ov-stat__label">{{ $t('luggageStrategy.selection') }}</span>
              <span v-if="strategieSelected === ''" class="ov-badge ov-badge--neutral">{{ $t('luggageStrategy.none') }}</span>
              <span v-else class="ov-badge ov-badge--success">
                {{ strategieSelected === 'typeStrategieBagagePoids' ? 'Par Poids' : 'Par Type' }}
              </span>
            </div>

            <div class="csb-tip">
              <div class="csb-tip__label">{{ $t('luggageStrategy.rule') }}</div>
              <div class="csb-tip__text">{{ $t('luggageStrategy.ruleText') }}</div>
            </div>
            <div class="csb-tip">
              <div class="csb-tip__label">Par Poids</div>
              <div class="csb-tip__text">
                Idéal pour facturer selon la quantité réelle transportée. Prix unitaire au kilo avec franchise incluse.
              </div>
            </div>
            <div class="csb-tip">
              <div class="csb-tip__label">Par Type</div>
              <div class="csb-tip__text">
                Idéal pour une tarification forfaitaire. Chaque catégorie (cabine, soute...) a un prix fixe.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
  </div>
</template>

<script>
import axios from "axios";
import {
  API_CREATE_STRATEGIE_BAGAGE,
  API_GET_STRATEGIE_PAR_COMPAGNIE,
  API_OBTENIR_REFERENCE_PAR_PAR_FAMILLE,
  HEADERS,
} from "@/components/globalConfig/globalConstConfig";
import StrategieBagagePoids from "./StrategieBagagePoids.vue";
import StrategieBagageType from "./StrategieBagageType.vue";
import { showErrorMessage, showSuccessMessage } from "@/modules/messages/messageProcess";
export default {
  name: "CreerStrategieBagage",
  components: {
    StrategieBagagePoids,
    StrategieBagageType,
  },
  data() {
    return {
      // COMMON VARIABLES
      errorMsg: null,
      successMsg: null,
      warningMsg: null,
      overlay: false,
      dialog: false,
      editedIndex: -1,

      // VARIABLE BAGAGES
      referenceBagage: {
        referenceFamilleDesignation: "referenceFamilleBagage",
      },

      strategieObject: {
        data: {},
      },

      typeStrategieBagage: {},

      objectToSendReferenceBagage: {
        datas: [],
      },

      referenceStrategieBagage: {
        referenceFamilleDesignation: "referenceFamilleTypeStrategieBagage",
      },

      objectToSendReferenceStrategieBagage: {
        datas: [],
      },

      referenceBagageTypeList: [],

      referenceBagageStrategyList: [],

      strategieSelected: "",

      objectToSendStrategieBagageParPoids: {
        datas: [],
      },

      strategieBagagePoidsModel: {
        data: {
          designation: null,
          typeStrategieBagageDesignation: null,
          strategieBagagePoidsDTO: {
            designation: null,
            prixKilo: null,
            nombreKiloNonTaxable: null,
          },
        },
      },

      strategieBagageTypeModel: {
        data: {
          designation: null,
          typeStrategieBagageDesignation: null,
          strategieBagageTypeDTO: {
            designation: null,
            prixBagageParTypeDTOS: [],
          },
        },
      },

      bagageTypeModel: {
        designation: null,
        prix: null,
        quantiteNonTaxable: null,
        typeBagageDesignation: null,
      },
    };
  },

  methods: {
    selectStrategie(designation) {
      this.strategieSelected = designation;
    },

    // Gérer la création réussie de la stratégie
    handleStrategieCreated(message) {
      // Afficher le message de succès
      if (message) {
        this.successMsg = message;
        showSuccessMessage();
      }
      // Recharger la liste des stratégies pour afficher la nouvelle
      this.getStrategieBagageList();
    },

    // ENVOYER LA STRATEGIE SÉLECTIONNÉE
    submitStrategieBagage() {
      if (this.strategieSelected == "typeStrategieBagagePoids") {
        this.$v.$touch();
        if (this.$v.strategieBagagePoidsModel.$invalid) {
          this.errorMsg = "Vous n'avez saisi les champs concernés";
          showErrorMessage();
        } else this.creerStrategieParPoids();
      } else {
        if (
          this.strategieBagageTypeModel.data.strategieBagageTypeDTO
            .prixBagageParTypeDTOS.length == 0
        ) {
          this.errorMsg = "Vous n'avez défini aucun type de bagage";
          showErrorMessage();
        } else this.creerStrategieParType();
      }
    },

    // CREER STRATEGIE PAR TYPE
    async creerStrategieParType() {
      this.overlay = true;
      this.strategieBagageTypeModel.data.designation = this.strategieSelected;
      this.strategieBagageTypeModel.data.typeStrategieBagageDesignation =
        this.strategieSelected;
      this.strategieBagageTypeModel.data.strategieBagageTypeDTO.designation =
        this.strategieSelected;

      await axios
        .post(API_CREATE_STRATEGIE_BAGAGE, this.strategieBagageTypeModel, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code == 800) {
              this.successMsg = response.data.status.message;
              showSuccessMessage();
            } else {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
            }
          } else {
            this.errorMsg = "Erreur , opération de création impossible";
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.overlay = false;
        });
    },

    // CREER STRATEGIE PAR POIDS
    async creerStrategieParPoids() {
      this.overlay = true;
      this.strategieBagagePoidsModel.data.designation = this.strategieSelected;
      this.strategieBagagePoidsModel.data.typeStrategieBagageDesignation =
        this.strategieSelected;
      this.strategieBagagePoidsModel.data.strategieBagagePoidsDTO.designation =
        this.strategieSelected;

      await axios
        .post(API_CREATE_STRATEGIE_BAGAGE, this.strategieBagagePoidsModel, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code == 800) {
              this.successMsg = response.data.status.message;
              showSuccessMessage();
            } else {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
            }
          } else {
            this.errorMsg = "Erreur , opération de création impossible";
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.overlay = false;
        });
    },

    //OBTENIR LA LISTE DES REFERENCES BAGAGES DISPONIBLE
    async obtenirReferenceTypeBagage() {
      this.objectToSendReferenceBagage.datas.push(this.referenceBagage);
      await axios
        .post(
          API_OBTENIR_REFERENCE_PAR_PAR_FAMILLE,
          this.objectToSendReferenceBagage,
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        )
        .then((response) => {
          this.referenceBagageTypeList = response.data.items;
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },

    //OBTENIR LA LISTE DES REFERENCES DES STRATEGIES DE GESTION DE BAGAGES DISPONIBLES
    async obtenirReferenceStrategyBagage() {
      this.objectToSendReferenceStrategieBagage.datas.push(
        this.referenceStrategieBagage
      );
      await axios
        .post(
          API_OBTENIR_REFERENCE_PAR_PAR_FAMILLE,
          this.objectToSendReferenceStrategieBagage,
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        )
        .then((response) => {
          this.referenceBagageStrategyList = response.data.items;
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },

    // RÉCUPERER LA STRATÉGIE DÉFINIE PAR COMPAGNIE
    async getStrategieBagageList() {
      await axios
        .post(API_GET_STRATEGIE_PAR_COMPAGNIE, this.strategieObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code != 800) {
              // Aucune stratégie définie
              this.typeStrategieBagage = undefined;
            } else {
              // Stratégie trouvée — mise à jour de l'affichage
              this.typeStrategieBagage = response.data.item;
            }
          } else {
            this.errorMsg = "Erreur";
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },
  },

  computed: {
    // Détail de la stratégie par poids en cours (évite les accès sur undefined dans le template)
    currentPoidsDTO() {
      return (this.typeStrategieBagage && this.typeStrategieBagage.strategieBagagePoidsDTO) || {};
    },
    // Lignes de prix de la stratégie par type en cours
    currentTypeLines() {
      const dto = this.typeStrategieBagage && this.typeStrategieBagage.strategieBagageTypeDTO;
      return (dto && dto.prixBagageParTypeDTOS) || [];
    },
  },

  mounted() {
    this.obtenirReferenceStrategyBagage();
    this.obtenirReferenceTypeBagage();
    this.getStrategieBagageList();
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/admin/ui";

/* TUILES DE CHOIX */
.csb-tiles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.csb-tile {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 16px;
  text-align: left;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: rgba(21, 158, 114, 0.45);
  }

  &--selected {
    border-color: $ov-green;
    background: rgba(21, 158, 114, 0.06);
    box-shadow: 0 0 0 3px rgba(21, 158, 114, 0.12);
  }
}

.csb-tile__check {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border-radius: 50%;
  border: 2px solid rgba(15, 23, 42, 0.2);
  transition: background 0.2s ease, border-color 0.2s ease;

  .csb-tile--selected & {
    background: $ov-green;
    border-color: $ov-green;
  }
}

.csb-tile__body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.csb-tile__title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 800;
  color: $ov-navy;
}

.csb-tile__desc {
  font-size: 13px;
  line-height: 1.5;
  color: $ov-muted;
}

.csb-tile__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

/* CONSEILS */
.csb-tip {
  padding: 12px 0;
  border-top: 1px solid $ov-line;
}

.csb-tip__label {
  font-size: 12px;
  font-weight: 800;
  color: $ov-navy;
  margin-bottom: 4px;
}

.csb-tip__text {
  font-size: 13px;
  line-height: 1.5;
  color: $ov-muted;
}

@media (max-width: 760px) {
  .csb-tiles {
    grid-template-columns: 1fr;
  }
}
</style>
